// Privacy Subject - core domain entity
export interface PrivacySubject {
  id: string
  email?: string
  email_normalized?: string
  phone?: string
  phone_normalized?: string
  first_name?: string
  last_name?: string
  opt_out_sale: boolean
  opt_out_share: boolean
  remove_data: boolean
  do_not_contact: boolean
  jurisdiction: PrivacyJurisdiction
  verification_status: VerificationStatus
  status: PrivacyStatus
  received_at: string
  due_date: string
  verified_at?: string
  completed_at?: string
  source?: string
  notes?: string
  created_at: string
  updated_at: string
}

// Enums as union types
export type PrivacyJurisdiction = 'ccpa' | 'gdpr' | 'other'
export type VerificationStatus = 'pending' | 'verified' | 'failed'
export type PrivacyStatus = 'received' | 'in_progress' | 'completed' | 'denied'

// Request types
export interface UpsertPrivacyRequest {
  email?: string
  phone?: string
  first_name?: string
  last_name?: string
  opt_out_sale: boolean
  opt_out_share: boolean
  remove_data: boolean
  do_not_contact: boolean
  jurisdiction: PrivacyJurisdiction
  received_at?: string
  source?: string
  notes?: string
}

export interface UpdatePrivacyRequest {
  phone?: string
  first_name?: string
  last_name?: string
  opt_out_sale?: boolean
  opt_out_share?: boolean
  remove_data?: boolean
  do_not_contact?: boolean
  verification_status?: VerificationStatus
  status?: PrivacyStatus
  notes?: string
}

// Response types
export interface UpsertPrivacyResponse {
  status: string
  subject: PrivacySubject
  created: boolean
}

export interface GetPrivacyResponse {
  status: string
  subject: PrivacySubject
}

export interface ListPrivacyResponse {
  status: string
  subjects: PrivacySubject[]
  total: number
  page: number
  page_size: number
}

export interface CheckPrivacyResponse {
  found: boolean
  opt_out_sale: boolean
  opt_out_share: boolean
  remove_data: boolean
  do_not_contact: boolean
}

export interface DeletePrivacyResponse {
  status: string
  message: string
}

export interface ImportPrivacyResponse {
  status: string
  total: number
  created: number
  updated: number
  failed: number
  errors?: ImportPrivacyError[]
}

export interface ImportPrivacyError {
  row: number
  email?: string
  phone?: string
  message: string
}

export interface RequestTypeCounts {
  opt_out_sale: number
  opt_out_share: number
  remove_data: number
  do_not_contact: number
}

export interface PrivacyStatsResponse {
  status: string
  total: number
  by_status: Record<string, number>
  by_verification: Record<string, number>
  by_jurisdiction: Record<string, number>
  by_request_type: RequestTypeCounts
  oldest_received_at?: string
  newest_received_at?: string
  oldest_created_at?: string
  newest_created_at?: string
  pending_due_soon: number
  overdue_count: number
}

// Query parameters
export interface ListPrivacyParams {
  page?: number
  page_size?: number
}

// Display helpers
export const JURISDICTION_LABELS: Record<PrivacyJurisdiction, string> = {
  ccpa: 'CCPA',
  gdpr: 'GDPR',
  other: 'Other',
}

export const STATUS_LABELS: Record<PrivacyStatus, string> = {
  received: 'Received',
  in_progress: 'In Progress',
  completed: 'Completed',
  denied: 'Denied',
}

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  failed: 'Failed',
}

// Audit log types
export interface FieldChange {
  old?: unknown
  new?: unknown
}

export interface PrivacyAuditEntry {
  id: string
  subject_id: string
  action: string
  user_id?: string
  user_email?: string
  changes?: Record<string, FieldChange>
  created_at: string
}

export interface ListAuditResponse {
  status: string
  entries: PrivacyAuditEntry[]
  total: number
}

// Display helper for field labels in audit log
export const FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  first_name: 'First Name',
  last_name: 'Last Name',
  opt_out_sale: 'Opt-Out Sale',
  opt_out_share: 'Opt-Out Share',
  remove_data: 'Remove Data',
  do_not_contact: 'Do Not Contact',
  jurisdiction: 'Jurisdiction',
  verification_status: 'Verification',
  status: 'Status',
  received_at: 'Received',
  due_date: 'Due Date',
  verified_at: 'Verified At',
  completed_at: 'Completed At',
  source: 'Source',
  notes: 'Notes',
}

// Display helper for action labels
export const ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
}
